import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PhoneEntryStep } from "./PhoneEntryStep";
import { OtpStep } from "./OtpStep";
import { useSharedLoginOtp } from "./handler/useSharedLoginOtp";
import { useReturnContext } from "./handler/useReturnContext";
import { useProvisionAccount } from "./handler/useProvisionAccount";
import { SharedLoginPreferredLanguage } from "../../api/sharedLogin.service";
import { OtpOutcome, SharedLoginStep, VerifyOtpOutcome } from "./types";

const SharedLogin: React.FC = () => {
  const { t, i18n } = useTranslation("sharedLogin");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const destination = searchParams.get("destination");

  const [step, setStep] = useState<SharedLoginStep>("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [verifiedPhoneToken, setVerifiedPhoneToken] = useState<
    string | undefined
  >(undefined);
  const [handoverErrorCode, setHandoverErrorCode] = useState<
    string | undefined
  >(undefined);
  const { sendOtp, verifyOtp, isSending, isVerifying } = useSharedLoginOtp();
  const { handOver, isWorking: isHandingOver } = useReturnContext();
  const { provisionAccount, isProvisioning } = useProvisionAccount();

  const handlePhoneSubmit = async (
    phone: string,
    consent: boolean,
  ): Promise<OtpOutcome> => {
    const result = await sendOtp(phone);
    if (result.success) {
      setPhoneNumber(phone);
      setMarketingConsent(consent);
      setStep("otp");
    }
    return result;
  };

  const handleResend = (): Promise<OtpOutcome> => sendOtp(phoneNumber);

  const handleBackToPhone = () => {
    setStep("phone");
  };

  const preferredLanguage: SharedLoginPreferredLanguage =
    i18n.language === "zh" ? "zhHK" : "en";

  const handleVerify = async (code: string): Promise<VerifyOtpOutcome> => {
    const result = await verifyOtp(phoneNumber, code);
    if (!result.success || !result.verifiedPhoneToken) return result;

    const provisionResult = await provisionAccount({
      verifiedPhoneToken: result.verifiedPhoneToken,
      ...(destination ? { signupOriginProduct: destination } : {}),
      marketingConsent,
      preferredLanguage,
    });
    if (!provisionResult.success) {
      return provisionResult.errorCode !== undefined
        ? { success: false, errorCode: provisionResult.errorCode }
        : { success: false };
    }

    setVerifiedPhoneToken(result.verifiedPhoneToken);
    setStep("verified");
    setHandoverErrorCode(undefined);

    if (!destination) {
      return result;
    }

    const handOverResult = await handOver(
      result.verifiedPhoneToken,
      destination,
    );
    if (!handOverResult.success || !handOverResult.returnContextToken) {
      setHandoverErrorCode(handOverResult.errorCode ?? "UNKNOWN");
      return result;
    }

    navigate(destination, {
      state: { returnContextToken: handOverResult.returnContextToken },
    });
    return result;
  };

  return (
    <div className="min-h-screen bg-app flex flex-col items-center px-6 pt-[74px] pb-6 md:pt-20 md:pb-20">
      <div className="w-full max-w-[480px] flex flex-col gap-5 md:gap-6">
        {step === "phone" && (
          <PhoneEntryStep
            onSubmit={handlePhoneSubmit}
            isSubmitting={isSending}
          />
        )}
        {step === "otp" && (
          <OtpStep
            phoneNumber={phoneNumber}
            onVerify={handleVerify}
            onResend={handleResend}
            onBack={handleBackToPhone}
            isVerifying={isVerifying || isProvisioning}
          />
        )}
        {step === "verified" && verifiedPhoneToken && (
          <div className="flex flex-col gap-2 rounded-2xl border-2 border-ink-12 bg-card p-5 md:p-8">
            <h1 className="font-lexend text-[24px]/[28px] font-bold tracking-[-0.8px] text-ink-100">
              {t("handover.heading")}
            </h1>
            <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60">
              {destination && isHandingOver
                ? t("handover.redirecting")
                : t("handover.subtitle")}
            </p>
            {handoverErrorCode && (
              <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
                {t("handover.errFailed")}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedLogin;
